import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
	Alert,
	Button,
	Icon,
	ProgressBarBox,
	Typography,
} from '@/components/common';
import { InputCheck } from '@/components/form';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';
import {
	UploadFile,
	StatusTypes,
	ActionRequiredCriteriaItem,
	PreAnalyzeStatus,
	ActionRequired,
} from '@/interfaces';
import { actionRequiredService, GET_REQUESTED_DOCUMENTS_CRITERIA } from '@/api';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { UploadContainer } from '@/components/common/upload';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import PdfViewer from '@/components/common/pdf-viewer/PdfViewer';
import { AISummary } from '@/components/common/ai-summary';
import { DocumentBadge } from '@/components/screens/lawyerapp/cases/case-clients/client-information/documentation/DocumentBadge';

import { logApiError } from '@/lib/logApiError';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import { StepTracker } from '@/components/modals/StepTracker';

// ─── Types ───────────────────────────────────────────────────────

type ModalStep = 'idle' | 'uploading' | 'reviewing';

interface ReviewCriteriaItem extends ActionRequiredCriteriaItem {
	isValid: boolean;
	errorMessage?: string;
}

interface Props {
	applicationId: number;
	actionRequiredId: number;
	actionRequiredSelected: ActionRequired | undefined;
	documentName: string;
	isOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	refetch: () => void;
	/** Shows the "Step 2 of 2" tracker — only when reached right after the
	 * identity-verification flow (Step 1). */
	showStepTracker?: boolean;
}

// ─── Component ───────────────────────────────────────────────────

export const ModalUploadDocumentation: React.FC<Props> = ({
	applicationId,
	actionRequiredId,
	documentName,
	isOpen,
	setIsModalOpen,
	refetch,
	actionRequiredSelected,
	showStepTracker,
}) => {
	const router = useRouter();
	const { pathname, query } = router;
	const { Modal, show, hide } = useModal();
	const { register } = useForm();
	const { toastSuccess, toastError } = useNotification();

	const [step, setStep] = useState<ModalStep>('idle');
	const [progress, setProgress] = useState(0);
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string>();
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Review state
	const [reviewStatus, setReviewStatus] = useState<PreAnalyzeStatus | null>(
		null
	);
	const [reviewSummary, setReviewSummary] = useState<string>('');
	const [reviewCriteria, setReviewCriteria] = useState<ReviewCriteriaItem[]>(
		[]
	);
	const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string>('');
	const [cannotProduceDocumentation, setCannotProduceDocumentation] =
		useState(false);

	const { data: actionRequiredCriteria } = useQuery({
		queryKey: [GET_REQUESTED_DOCUMENTS_CRITERIA, actionRequiredId],
		queryFn: () =>
			actionRequiredService.actionRequiredCriteria(actionRequiredId),
		enabled: !!actionRequiredId,
	});

	const criteria = (actionRequiredCriteria?.data?.criteria ?? []).filter(
		(item) => item.key !== 'prompt'
	);
	const validFileTypes = [
		'image/jpeg',
		'image/jpg',
		'image/png',
		'application/pdf',
	];

	useEffect(() => {
		if (isOpen) {
			show();
		}
	}, [isOpen]);

	// ─── Handlers ────────────────────────────────────────────────

	const deleteQuery = (): void => {
		delete query['type'];
		router.replace({ pathname, query });
	};

	const handleCloseModal = async (): Promise<void> => {
		hide();
		refetch();
		deleteQuery();
		setIsModalOpen(false);
		resetState();
	};

	const resetState = () => {
		setStep('idle');
		setProgress(0);
		setFile(null);
		setError(undefined);
		setReviewStatus(null);
		setReviewSummary('');
		setReviewCriteria([]);
		setDocumentPreviewUrl('');
		setCannotProduceDocumentation(false);
	};

	const simulateProgress = async (max: number): Promise<void> => {
		return new Promise((resolve) => {
			let currentProgress = 0;
			const interval = setInterval(() => {
				currentProgress += 10;
				setProgress(currentProgress);
				if (currentProgress >= max) {
					clearInterval(interval);
					resolve();
				}
			}, 300);
		});
	};

	const handleDropFile = (e: React.DragEvent<HTMLDivElement>): void => {
		e.preventDefault();
		e.stopPropagation();
		const droppedFile = e.dataTransfer.files[0];
		setProgress(0);
		handleUploadFile(droppedFile);
	};

	const handleUploadFile = async (uploadedFile: File): Promise<void> => {
		setProgress(0);
		setError(undefined);

		if (!validateImageSize(uploadedFile)) return;

		if (!isValidFileTypes(uploadedFile)) return;

		setStep('uploading');
		await simulateProgress(90);

		let response;
		try {
			response = await actionRequiredService.actionRequiredPreAnalyzeFile(
				uploadedFile,
				actionRequiredId
			);
		} catch (error) {
			setStep('idle');
			setProgress(0);
			setFile(null);
			return;
		}

		// The document was received by the server even if analysis itself
		// failed (e.g. AI provider outage) — still show the preview instead
		// of reverting to the upload dropzone as if nothing was uploaded.
		setFile(uploadedFile);
		setProgress(100);

		const {
			status,
			feedback,
			details,
			error: analysisError,
		} = response?.data ?? {
			status: 'needs_review' as PreAnalyzeStatus,
			feedback: '',
			details: null,
			error: 'Analysis unavailable',
		};

		const detailEntries = Object.entries(details ?? {});
		const matched = criteria.map((c) => {
			const titleNorm = c.title.toLowerCase().replace(/\s+/g, '');
			const match = detailEntries.find(([k]) => {
				const kNorm = k.toLowerCase().replace(/\s+/g, '');
				return kNorm.includes(titleNorm) || titleNorm.includes(kNorm);
			});
			const detail = match?.[1];
			return {
				...c,
				isValid: detail ? detail.is_valid : true,
				errorMessage: detail && !detail.is_valid ? detail.reason : undefined,
			} as ReviewCriteriaItem;
		});

		const statusLower = status.toLowerCase();
		const normalizedStatus: PreAnalyzeStatus = analysisError
			? 'needs_review'
			: statusLower.includes('invalid')
			? 'invalid'
			: statusLower.includes('valid')
			? 'validated'
			: 'incomplete';

		setReviewStatus(normalizedStatus);
		setReviewSummary(
			analysisError
				? 'We could not automatically review this document. It has been uploaded and will be manually reviewed.'
				: feedback
		);
		setReviewCriteria(matched);

		const blobUrl = URL.createObjectURL(uploadedFile);
		const previewUrl = uploadedFile.type.startsWith('image/')
			? `${blobUrl}#${uploadedFile.name}`
			: blobUrl;
		setDocumentPreviewUrl(previewUrl);
		setStep('reviewing');
	};

	const handleSendFile = async (): Promise<void> => {
		const canSkip = cannotProduceDocumentation && !file;
		if (!canSkip && !(file && applicationId)) return;

		try {
			setIsSubmitting(true);
			if (canSkip) {
				await actionRequiredService.skipDocument(actionRequiredId);
			} else {
				const data: UploadFile = {
					applicationId: applicationId,
					doc: file as File,
					fileName: (file as File).name,
					actionRequiredId: actionRequiredId,
				};
				await actionRequiredService.postUploadFile(data);
			}
			toastSuccess('The required action has been successfully provided.');
			handleCloseModal();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleUploadNewFile = () => {
		if (documentPreviewUrl) {
			URL.revokeObjectURL(documentPreviewUrl);
		}
		setFile(null);
		setProgress(0);
		setStep('idle');
		setReviewStatus(null);
		setReviewSummary('');
		setReviewCriteria([]);
		setDocumentPreviewUrl('');
	};

	const handleRemoveFile = () => {
		setFile(null);
		setProgress(0);
		setStep('idle');
	};

	const validateImageSize = (file: File): boolean => {
		if (file.size > 25 * 1000 * 1024) {
			setProgress(0);
			setError(
				'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.'
			);
			logApiError({
				status: 400,
				message:
					'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.',
				config: { data: null, url: router.asPath },
			});
			return false;
		}
		return true;
	};

	const isValidFileTypes = (file: File): boolean => {
		if (!validFileTypes.includes(file.type)) {
			setProgress(0);
			setError(
				'Select a file in the correct format such as pdf, png, jpg or jpeg'
			);
			logApiError({
				status: 400,
				message:
					'Select a file in the correct format such as pdf, png, jpg or jpeg',
				config: { data: null, url: router.asPath },
			});
			return false;
		}
		return true;
	};

	const getFileName = (): string => {
		if (
			progress === 100 &&
			file &&
			typeof file !== 'string' &&
			'name' in file
		) {
			return file.name;
		}
		return '';
	};

	const getSummaryVariant = (): 'green' | 'pink' => {
		return reviewStatus === 'validated' ? 'green' : 'pink';
	};

	return (
		<Modal
			withXButton={step !== 'uploading'}
			title={
				showStepTracker || step === 'reviewing' ? (
					<div className="flex items-center justify-between w-full">
						<Typography type="semibold-20" className="!text-xl">
							{step === 'reviewing'
								? 'Review Document'
								: 'Upload Documentation'}
						</Typography>
						{showStepTracker && <StepTracker currentStep={2} totalSteps={2} />}
					</div>
				) : undefined
			}
			classNameTitle="text-left"
			classNameCardContainer="!w-[96%] sm:!w-[90%] !max-w-[800px] bg-white"
			classNameContainerChildren="!px-0"
			maxHeight="85vh"
			onClose={handleCloseModal}
			avoidClose={step === 'uploading'}
		>
			<div className="flex flex-col">
				{!showStepTracker && step !== 'reviewing' && (
					<Typography
						type="semibold-20"
						className={clsx(
							'!text-xl text-left mb-5 xl:mb-5',
							documentName.length > 400 && '!text-sm'
						)}
					>
						Upload Documentation
					</Typography>
				)}
				<div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
					{/* ─── Left Panel ─── */}
					<div className="w-full lg:w-1/2 flex flex-col text-left">
						{step === 'reviewing' ? (
							<ReviewingLeftPanel
								documentName={documentName}
								reviewStatus={reviewStatus}
								reviewSummary={reviewSummary}
								reviewCriteria={reviewCriteria}
								getSummaryVariant={getSummaryVariant}
							/>
						) : (
							<IdleLeftPanel criteria={criteria} />
						)}
					</div>

					{/* ─── Right Panel ─── */}
					<div className="w-full lg:w-1/2">
						{step === 'reviewing' && documentPreviewUrl ? (
							<PdfViewer pdfUrl={documentPreviewUrl} />
						) : (
							<div className="flex flex-col items-end justify-center !text-left">
								<div
									className={clsx(
										'border-2 rounded-xl border-dashed border-[#E5E7EB] bg-[#F9FAFB] w-full max-w-[340px] min-h-[246px] flex flex-col items-center justify-center',
										{
											'border-opacity-primary': !error,
											'border-red-600': error,
										}
									)}
									onDrop={handleDropFile}
									onDragOver={(e) => e.preventDefault()}
								>
									<div className="py-6 px-8 flex flex-col items-center gap-3">
										{/* Upload icon */}
										<div className="size-14 rounded-full bg-[#8A5AFB]/10 flex items-center justify-center">
											<Icon
												src={Icons.uploadCloud}
												width={30}
												height={24}
												className="text-primary"
											/>
										</div>

										{/* Upload text */}
										{step !== 'uploading' && (
											<div className="text-center">
												<Typography type="regular-14">
													Drag and drop your file here, or click to
													<br />
													browse from your device
												</Typography>
											</div>
										)}

										{/* Progress bar */}
										{(progress > 0 || step === 'uploading') && (
											<div className="w-48">
												<ProgressBarBox
													percent={progress}
													handleRemove={handleRemoveFile}
													fileName={getFileName()}
													isShowPrevFile={false}
												/>
											</div>
										)}

										{/* Upload button */}
										{progress === 0 && !file && step !== 'uploading' && (
											<div className="flex flex-col items-center gap-3">
												<UploadContainer
													id="upload-documentation"
													setFile={handleUploadFile}
													accept={validFileTypes.join(',')}
													disabled={cannotProduceDocumentation}
												>
													<div
														className={clsx(
															'flex items-center bg-primary rounded-full text-base font-semibold text-white gap-2 px-6 h-[30px] py-4',
															cannotProduceDocumentation
																? 'cursor-not-allowed opacity-50'
																: 'cursor-pointer'
														)}
													>
														Upload
														<Icon
															src={Icons.fileAltWhite}
															className="!w-4 !h-4 !text-white"
														/>
													</div>
												</UploadContainer>
												<Typography
													type="regular-12"
													className="text-gray-35 text-center"
												>
													PDF, PNG, JPG up to 25 MB
												</Typography>
											</div>
										)}

										{/* Error message */}
										{error && (
											<Typography
												type="regular-12"
												text={error}
												className="text-main-red max-w-60 mx-auto"
											/>
										)}
									</div>
								</div>
								{(actionRequiredSelected?.criteria?.skip_document ||
									actionRequiredSelected?.skip_document) && (
									<>
										<div className="border-2 border-[#E5E7EB] bg-[#F9FAFB] p-6 rounded-xl w-full max-w-[340px] min-h-[122px] mt-2">
											<InputCheck
												name="cannot-produce-documentation"
												register={register}
												checked={cannotProduceDocumentation}
												disabled={!!file || step === 'uploading'}
												className="!items-start"
												classNameInput="!size-5"
												onChangeCustom={(e) =>
													setCannotProduceDocumentation(
														(e.target as HTMLInputElement).checked
													)
												}
											>
												I have made a good-faith effort to locate the requested
												documentation and am unable to produce it at this time.
											</InputCheck>
										</div>
										{cannotProduceDocumentation && (
											<Alert
												icon={
													<Image
														src={Icons.info}
														alt="image"
														width={18}
														height={18}
														className="shrink-0 mt-1 !size-5"
													/>
												}
												typography="regular-12"
												className="!w-full !max-w-[340px] !rounded-lg !p-3 !gap-3 !bg-[#2084E71A] mt-2"
												classNameDescription="!text-[#3957F4]"
												description="I understand that without supporting documentation, the value of my claim may be affected."
											/>
										)}
									</>
								)}
							</div>
						)}

						{/* ─── Action Buttons (desktop) ─── */}
						<div
							className={clsx(
								'hidden lg:flex gap-3 items-center justify-end mt-3 pt-6 w-full',
								{
									'max-w-[410px] ml-auto': !(
										step === 'reviewing' && reviewStatus
									),
								}
							)}
						>
							<ActionButtons
								step={step}
								reviewStatus={reviewStatus}
								isSubmitting={isSubmitting}
								file={file}
								cannotProduceDocumentation={cannotProduceDocumentation}
								handleUploadNewFile={handleUploadNewFile}
								handleSendFile={handleSendFile}
							/>
						</div>
					</div>
				</div>

				{/* ─── Action Buttons (mobile) ─── */}
				<div className="flex lg:hidden flex-col gap-3 justify-end pt-6">
					<ActionButtons
						step={step}
						reviewStatus={reviewStatus}
						isSubmitting={isSubmitting}
						file={file}
						cannotProduceDocumentation={cannotProduceDocumentation}
						handleUploadNewFile={handleUploadNewFile}
						handleSendFile={handleSendFile}
					/>
				</div>
			</div>
		</Modal>
	);
};

// ─── Sub-Components ──────────────────────────────────────────────

interface ActionButtonsProps {
	step: ModalStep;
	reviewStatus: PreAnalyzeStatus | null;
	isSubmitting: boolean;
	file: File | null;
	cannotProduceDocumentation: boolean;
	handleUploadNewFile: () => void;
	handleSendFile: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
	step,
	reviewStatus,
	isSubmitting,
	file,
	cannotProduceDocumentation,
	handleUploadNewFile,
	handleSendFile,
}) => {
	if (step === 'reviewing' && reviewStatus === 'validated') {
		return (
			<>
				<Button
					size="fit"
					type="button"
					decoration="line-primary"
					onClick={handleUploadNewFile}
				>
					Upload New File
				</Button>
				<Button
					size="fit"
					type="button"
					decoration="primary"
					onClick={handleSendFile}
					disabled={isSubmitting}
				>
					Send
				</Button>
			</>
		);
	}

	if (step === 'reviewing' && reviewStatus) {
		return (
			<>
				<Button
					size="fit"
					type="button"
					decoration="line-primary"
					onClick={handleSendFile}
					disabled={isSubmitting}
				>
					Send Anyway
				</Button>
				<Button
					size="fit"
					type="button"
					decoration="primary"
					onClick={handleUploadNewFile}
				>
					Upload New File
				</Button>
			</>
		);
	}

	return (
		<Button
			size="fit"
			type="button"
			decoration="primary"
			label="Send"
			icon={Icons.sendTwo}
			iconRight
			onClick={handleSendFile}
			classNameButton="!size-5"
			disabled={
				(!file && step === 'idle' && !cannotProduceDocumentation) ||
				step === 'uploading' ||
				isSubmitting
			}
		/>
	);
};

interface IdleLeftPanelProps {
	criteria: ActionRequiredCriteriaItem[];
}

const IdleLeftPanel: React.FC<IdleLeftPanelProps> = ({ criteria }) => {
	return (
		<div className="">
			<div>
				<Typography type="semibold-20" className="!text-xl">
					Acceptance Criteria
				</Typography>
				<Typography type="regular-14" className="text-black mt-4">
					Please ensure your document clearly shows the following information to
					avoid delays in processing.
				</Typography>
			</div>

			<div className="mt-4 space-y-4">
				{criteria.map((item, index) => (
					<CriteriaItem key={index} item={item} />
				))}
			</div>
		</div>
	);
};

interface ReviewingLeftPanelProps {
	documentName: string;
	reviewStatus: PreAnalyzeStatus | null;
	reviewSummary: string;
	reviewCriteria: ReviewCriteriaItem[];
	getSummaryVariant: () => 'green' | 'pink';
}

const ReviewingLeftPanel: React.FC<ReviewingLeftPanelProps> = ({
	documentName,
	reviewStatus,
	reviewSummary,
	reviewCriteria,
	getSummaryVariant,
}) => {
	return (
		<>
			<div className="flex items-start justify-between">
				<Typography
					type="semibold-18"
					className={clsx(documentName.length > 400 && '!text-sm')}
				>
					{documentName}
				</Typography>
				{reviewStatus && (
					<DocumentBadge
						status={
							(reviewStatus === 'needs_review'
								? 'Needs Review'
								: reviewStatus.toUpperCase()) as StatusTypes
						}
					/>
				)}
			</div>

			{reviewSummary && (
				<div className="mt-4">
					<AISummary summary={reviewSummary} variant={getSummaryVariant()} />
				</div>
			)}

			<div
				className={clsx('mt-4 space-y-3', {
					'overflow-y-auto scroll-custom sm:max-h-44 2xl:max-h-[230px] [@media(max-height:720px)]:max-h-32':
						documentName.length > 400,
				})}
			>
				{reviewCriteria.map((item, index) => (
					<CriteriaItem key={index} item={item} />
				))}
			</div>
		</>
	);
};

interface CriteriaItemProps {
	item: ActionRequiredCriteriaItem | ReviewCriteriaItem;
}

const isReviewItem = (
	item: ActionRequiredCriteriaItem | ReviewCriteriaItem
): item is ReviewCriteriaItem => {
	return 'isValid' in item;
};

const CriteriaItem: React.FC<CriteriaItemProps> = ({ item }) => {
	const reviewed = isReviewItem(item);
	const isValid = reviewed ? item.isValid : true;

	const title = item.key === 'client name' ? 'Client Name' : item.title;
	return (
		<div className="flex items-center gap-3">
			<div className="mt-0.5 shrink-0">
				{!reviewed ? (
					<div className="size-6 rounded-full bg-purple-150 flex items-center justify-center">
						<Icon src={Icons.checkWhite} width={16} height={16} />
					</div>
				) : (
					<Icon
						src={isValid ? Icons.checkCircle : Icons.closeCircle}
						width={24}
						height={24}
						className={clsx({
							'!text-green-60': isValid,
							'!text-main-red': !isValid,
						})}
					/>
				)}
			</div>
			<div>
				<Typography type="regular-16">{title}</Typography>
				{reviewed && !isValid && item.errorMessage ? (
					<Typography type="regular-14" className="text-main-red">
						{item.errorMessage}
					</Typography>
				) : (
					<Typography type="regular-14" className="text-gray-35">
						{item.instruction}
					</Typography>
				)}
			</div>
		</div>
	);
};
